import * as Yup from 'yup';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
// formik validation
import { Form, FormikProvider, useFormik } from 'formik';
// @mui
import {
  Stack,
  IconButton,
  InputAdornment,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Chip,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// redux
import { useDispatch } from 'react-redux';
import { registrationAccount } from './AccountCreationAction';
// components
import Iconify from '../../../components/iconify';
import { PASSWORD_REGEX } from '../../../Constants/RegexConstants';

// ----------------------------------------------------------------------

export default function AccountCreationForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // state variables
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [serviceMappings, setServiceMappings] = useState([]); // [{years: 2, leaves: 15}, ...]
  
  const weekDays = [
    { value: 'sunday', label: 'Sunday' },
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
  ];

  // Schema for validation
  const RegisterSchema = Yup.object().shape({
    companyName: Yup.string().min(2, 'Too Short!').required('Company name is required'),
    userName: Yup.string().min(2, 'Too Short!').required('User name is required'),
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string()
      .required('Password is required')
      .matches(
        PASSWORD_REGEX,
        'Password should contain at least one number and one special character having length between 6 To 16 characters.'
      ),
    confirmPassword: Yup.string()
      .required('Confirm password is required')
      .oneOf([Yup.ref('password')], 'Passwords do not match'),
    userTimezone: Yup.string().default('Asia/Calcutta'),
    weeklyHolidays: Yup.array()
      .of(Yup.string().oneOf(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']))
      .min(1, 'At least one weekly holiday must be selected')
      .required('Weekly holidays are required'),
    yearlyPaidLeaves: Yup.number()
      .min(0, 'Yearly paid leaves must be 0 or greater')
      .integer('Yearly paid leaves must be a whole number')
      .required('Yearly paid leaves is required'),
    increaseLeavesByService: Yup.boolean(),
    serviceYearLeaveMapping: Yup.object().when('increaseLeavesByService', {
      is: true,
      then: (schema) => schema.required('Service year leave mapping is required when increasing leaves by service'),
      otherwise: (schema) => schema.nullable(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      companyName: '',
      userName: '',
      email: '',
      password: '',
      confirmPassword: '',
      userTimezone: 'Asia/Calcutta',
      weeklyHolidays: ['saturday', 'sunday'],
      yearlyPaidLeaves: 12,
      increaseLeavesByService: false,
      serviceYearLeaveMapping: {},
    },
    validationSchema: RegisterSchema,

    onSubmit: (values) => {
      const body = { ...values };
      delete body.confirmPassword;
      dispatch(registrationAccount(body))
        .then(({ value }) => {
          if (value.data.status === 'success') {
            navigate('/auth/login', { replace: true });
            toast.success('Registration Successful');
            toast.success('Please check your email to confirm your account');
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message ?? 'Error while creation');
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  const { errors, touched, handleSubmit, getFieldProps, isSubmitting, setSubmitting, values, setFieldValue } = formik;

  // Handle adding service year mapping
  const handleAddServiceMapping = () => {
    setServiceMappings([...serviceMappings, { years: '', leaves: '' }]);
  };

  // Handle removing service year mapping
  const handleRemoveServiceMapping = (index) => {
    const newMappings = serviceMappings.filter((_, i) => i !== index);
    setServiceMappings(newMappings);
    updateServiceYearMapping(newMappings);
  };

  // Handle service mapping change
  const handleServiceMappingChange = (index, field, value) => {
    const newMappings = [...serviceMappings];
    newMappings[index][field] = value;
    setServiceMappings(newMappings);
    updateServiceYearMapping(newMappings);
  };

  // Update serviceYearLeaveMapping in formik
  const updateServiceYearMapping = (mappings) => {
    const mappingObj = {};
    mappings.forEach((mapping) => {
      if (mapping.years && mapping.leaves) {
        mappingObj[parseFloat(mapping.years)] = parseFloat(mapping.leaves);
      }
    });
    setFieldValue('serviceYearLeaveMapping', mappingObj);
  };

  return (
    <>
      <FormikProvider value={formik}>
        <Form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              name="companyName"
              label="Company Name"
              {...getFieldProps('companyName')}
              error={touched.companyName && Boolean(errors.companyName)}
              helperText={touched.companyName && errors.companyName}
            />
            <TextField
              name="userName"
              label="User Name"
              {...getFieldProps('userName')}
              error={touched.userName && Boolean(errors.userName)}
              helperText={touched.userName && errors.userName}
            />

            <TextField
              name="email"
              label="Email address"
              {...getFieldProps('email')}
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email}
            />

            <TextField
              name="password"
              label="Password"
              {...getFieldProps('password')}
              error={touched.password && Boolean(errors.password)}
              helperText={touched.password && errors.password}
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              name="confirmPassword"
              label="Confirm Password"
              {...getFieldProps('confirmPassword')}
              error={touched.confirmPassword && Boolean(errors.confirmPassword)}
              helperText={touched.confirmPassword && errors.confirmPassword}
              type={showConfirmPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      <Iconify icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Leave Management Settings */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Leave Management Settings
              </Typography>

              {/* Weekly Holidays */}
              <FormControl fullWidth error={touched.weeklyHolidays && Boolean(errors.weeklyHolidays)} sx={{ mb: 2 }}>
                <InputLabel>Weekly Holidays *</InputLabel>
                <Select
                  multiple
                  value={values.weeklyHolidays || []}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFieldValue('weeklyHolidays', typeof value === 'string' ? value.split(',') : value);
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const day = weekDays.find((d) => d.value === value);
                        return <Chip key={value} label={day ? day.label : value} size="small" />;
                      })}
                    </Box>
                  )}
                  label="Weekly Holidays *"
                >
                  {weekDays.map((day) => (
                    <MenuItem key={day.value} value={day.value}>
                      {day.label}
                    </MenuItem>
                  ))}
                </Select>
                {touched.weeklyHolidays && errors.weeklyHolidays && (
                  <FormHelperText>{errors.weeklyHolidays}</FormHelperText>
                )}
                {!errors.weeklyHolidays && (
                  <FormHelperText>Select the days that are weekly holidays for your company</FormHelperText>
                )}
              </FormControl>

              {/* Yearly Paid Leaves */}
              <TextField
                name="yearlyPaidLeaves"
                label="Yearly Paid Leaves *"
                type="number"
                {...getFieldProps('yearlyPaidLeaves')}
                error={touched.yearlyPaidLeaves && Boolean(errors.yearlyPaidLeaves)}
                helperText={touched.yearlyPaidLeaves ? errors.yearlyPaidLeaves : 'Number of paid leaves per year for employees'}
                fullWidth
                sx={{ mb: 2 }}
                inputProps={{ min: 0, step: 1 }}
              />

              {/* Increase Leaves by Service */}
              {/* <FormControlLabel
                control={
                  <Checkbox
                    checked={values.increaseLeavesByService || false}
                    onChange={(e) => {
                      setFieldValue('increaseLeavesByService', e.target.checked);
                      if (!e.target.checked) {
                        setServiceMappings([]);
                        setFieldValue('serviceYearLeaveMapping', {});
                      }
                    }}
                  />
                }
                label="Increase paid leaves based on years of service"
                sx={{ mb: 2 }}
              /> */}

              {/* Service Year Leave Mapping */}
              {values.increaseLeavesByService && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Define additional leaves to add to base leaves based on years of service completed before Jan 1st.
                    <br />
                    Example: If base is 12 and user completes 2 years → enter "2" as years and "2" as leaves (total = 12 + 2 = 14).
                    <br />
                    Years are rounded down to nearest 0.5 (2.0-2.49 → 2.0, 2.5-2.99 → 2.5).
                  </Typography>
                  {serviceMappings.map((mapping, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                      <TextField
                        label="Years of Service"
                        type="number"
                        value={mapping.years}
                        onChange={(e) => handleServiceMappingChange(index, 'years', e.target.value)}
                        sx={{ width: '150px' }}
                        inputProps={{ min: 0, step: 0.5 }}
                        helperText="e.g., 2 or 0.5"
                      />
                      <TextField
                        label="Paid Leaves"
                        type="number"
                        value={mapping.leaves}
                        onChange={(e) => handleServiceMappingChange(index, 'leaves', e.target.value)}
                        sx={{ width: '150px' }}
                        inputProps={{ min: 0, step: 0.5 }}
                        helperText="e.g., 15"
                      />
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveServiceMapping(index)}
                        sx={{ mt: 1 }}
                      >
                        <Iconify icon="eva:trash-2-outline" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddServiceMapping}
                    startIcon={<Iconify icon="eva:plus-fill" />}
                  >
                    Add Service Year Mapping
                  </Button>
                  {touched.serviceYearLeaveMapping && errors.serviceYearLeaveMapping && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {errors.serviceYearLeaveMapping}
                    </FormHelperText>
                  )}
                </Box>
              )}
            </Box>
          </Stack>

          <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
            Create Account
          </LoadingButton>
        </Form>
      </FormikProvider>
    </>
  );
}
